import type { SupabaseClient } from '../../lib/supabase';
import type { MyTask } from './types';

/**
 * The cross-project read behind My Tasks. One request: every task assigned
 * to this person that Row Level Security lets them see, with the owning
 * project's name and the task's workflow status embedded. Completed tasks
 * stay out at the database, not in the browser. Tasks in an archived project
 * are dropped after the fetch, for the reason isLiveWork gives.
 */

// One literal, deliberately unwrapped: PostgREST infers the row type from the
// exact string, and concatenating it collapses that inference to unknown.
// prettier-ignore
const MY_TASK_COLUMNS = 'id, project_id, deal_id, parent_task_id, title, priority, due_date, is_completed, projects(name, is_archived), deals(name), project_statuses(name, color_key)';

function fail(message: string): never {
  throw new Error(message);
}

/**
 * Archiving a project retires the work inside it.
 *
 * Kyu, 8 September: "if you archive a project, everything in it is archived as
 * well." archive_project sets three columns on the project row and touches
 * nothing else — by design, since the project row is the single place
 * archived-ness lives and copying it onto every task would only create two
 * facts that can disagree. But nothing on this path ever read that flag, so an
 * archived project's open tasks sat on their assignee's list with no action
 * left that could clear them: archiving did not, and completing them is the
 * opposite of what archiving means.
 *
 * Decided here rather than in the request because the only PostgREST spelling
 * that filters on an embedded column is `projects!inner`, and an inner join
 * would also drop every DEAL task, which has no project at all. Those are live
 * work and belong on the list. The rows are already narrowed to one assignee's
 * open tasks, so the cost of deciding it here is a handful of rows.
 */
function isLiveWork(task: MyTask): boolean {
  return task.projects === null ? true : !task.projects.is_archived;
}

export async function fetchMyTasks(client: SupabaseClient, userId: string): Promise<MyTask[]> {
  const { data, error } = await client
    .from('tasks')
    .select(MY_TASK_COLUMNS)
    .eq('assignee_id', userId)
    .eq('is_completed', false)
    .order('due_date', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: true });

  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data.filter(isLiveWork) : [];
}
